import { useQuery } from "@apollo/client";
import {
  ArrowNarrowLeft,
  ArrowNarrowRight,
  NotificationBox,
  Plus,
} from "@untitled-ui/icons-react";
import { range } from "lodash";
import moment from "moment";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  GetSidebarDataQueryQuery,
  GetSidebarDataQueryQueryVariables,
  MeetingDialogFragmentFragment,
  SidebarMeetingFragmentFragment,
} from "types/graphql-schema";

import MeetingDialog from "@apps/meeting-dialog/meeting-dialog";
import { currentUserVar } from "@cache/cache";
import Link from "@components/link/link";
import AppLink from "@components/link/link";
import { useLink } from "@components/link/link-helpers";
import Tooltip from "@components/tooltip/tooltip";
import { TopicflowIcon } from "@components/topicflow-icon/topicflow-icon";
import useDebounce from "@components/use-debounce/use-debounce";
import { resetDateSeconds } from "@helpers/date";
import { assertEdgesNonNull, removeCurrentYear } from "@helpers/helpers";
import useVisibilityControlledInterval from "@helpers/hooks/use-interval";
import usePageVisible, { isPageHidden } from "@helpers/hooks/use-page-visible";

import getSidebarDataQuery from "../graphql/get-sidebar-data-query";
import SidebarMeeting from "./sidebar-meeting";

const SidebarScheduledMeetingsList = ({
  meetings,
  meetingDate,
}: {
  meetings: SidebarMeetingFragmentFragment[];
  meetingDate: string;
}) => {
  // re-render the list only when the minute changes but check every 2s
  const [now, setNow] = useState(resetDateSeconds(new Date()).getTime());
  useVisibilityControlledInterval(() => {
    setNow(resetDateSeconds(new Date()).getTime());
  }, 1000);
  const currentUser = currentUserVar();
  const hasCalendarCredentials = currentUser.syncCredentials?.edges.find(
    (edge) => edge?.node?.hasValidCalendarCredentials,
  );
  const isToday = moment(meetingDate).isSame(moment(), "day");

  return meetings.length > 0 ? (
    meetings.map((meeting) => (
      <SidebarMeeting
        key={meeting.id}
        title={meeting.title}
        videoConferenceUrl={meeting.videoConferenceUrl}
        videoConferenceType={meeting.videoConferenceType}
        meetingId={meeting.id}
        meetingGroupId={meeting.meetingGroup?.id}
        draft={meeting.draft}
        startDatetime={meeting.startDatetime}
        endDatetime={meeting.endDatetime}
        now={now}
      />
    ))
  ) : hasCalendarCredentials ? (
    <div className="text-gray-500 py-4 px-2 flex flex-col gap-2">
      <div className="flex justify-center items-center">
        <div className="flex p-1 bg-gray-100 rounded-md">
          <TopicflowIcon type="meeting" className="h-5 w-5 text-gray-300" />
        </div>
      </div>
      <div className="text-gray-800 text-sm text-center font-medium">
        No Meetings{" "}
        {isToday ? (
          "Today"
        ) : (
          <>
            on
            <br />
            {removeCurrentYear(
              moment(meetingDate).format("dddd, MMMM D, YYYY"),
            )}
          </>
        )}
      </div>
    </div>
  ) : null;
};

const SidebarMeetings = () => {
  const currentUser = currentUserVar();
  const lastLoadingCount = useRef(3);
  const [meetingDate, setMeetingDate] = useState(moment().format("YYYY-MM-DD"));
  const debouncedMeetingDate = useDebounce(meetingDate, 500);
  const lastTimeVisible = useRef<null | string>(null);
  const pageIsVisible = usePageVisible(1000);
  const getTimeAgo = useCallback(
    function updateTimeAgo() {
      return {
        startDatetimeGte: moment(debouncedMeetingDate).startOf("day").format(),
        startDatetimeLte: moment(debouncedMeetingDate).endOf("day").format(),
      };
    },
    [debouncedMeetingDate],
  );
  const { data, loading } = useQuery<
    GetSidebarDataQueryQuery,
    GetSidebarDataQueryQueryVariables
  >(getSidebarDataQuery, {
    pollInterval: 5 * 60 * 1000, // 5 minutes
    skipPollAttempt: isPageHidden,
    variables: {
      ...getTimeAgo(),
      participants: [currentUser.id],
    },
  });

  const link = useLink();

  const handleClickPreviousDay = useCallback(() => {
    setMeetingDate(moment(meetingDate).subtract(1, "day").format("YYYY-MM-DD"));
  }, [meetingDate]);
  const handleClickNextDay = useCallback(() => {
    setMeetingDate(moment(meetingDate).add(1, "day").format("YYYY-MM-DD"));
  }, [meetingDate]);
  const handleClickToday = () => {
    setMeetingDate(moment().format("YYYY-MM-DD"));
  };

  const [showCreateMeetingModal, setShowCreateMeetingModal] = useState(false);

  const meetings = (
    data?.meetings ? assertEdgesNonNull(data.meetings) : []
  ).filter(({ meetingGroup }) => meetingGroup); // clean up meeting with no meeting groups
  const unscheduledMeetings = data?.unscheduledMeetings
    ? assertEdgesNonNull(data.unscheduledMeetings)
    : []; // clean up meeting with no meeting groups

  useEffect(() => {
    if (!loading) {
      lastLoadingCount.current = Math.min(Math.max(1, meetings.length), 3);
    }
  }, [meetings, loading]);

  useEffect(
    function handleResetMeetingDate() {
      let st: undefined | number;
      if (
        pageIsVisible &&
        lastTimeVisible.current !== null &&
        (!moment().isSame(lastTimeVisible.current, "day") ||
          moment().isAfter(moment(lastTimeVisible.current).add(30, "minutes")))
      ) {
        setMeetingDate(moment().format("YYYY-MM-DD"));
      }
      if (!pageIsVisible) {
        lastTimeVisible.current = moment().format();
      }
      return () => {
        if (st) clearTimeout(st);
      };
    },
    [pageIsVisible],
  );

  return (
    <div>
      {showCreateMeetingModal && (
        <MeetingDialog
          refetchQueries={[getSidebarDataQuery]}
          onSaved={({
            meeting,
          }: {
            meeting: MeetingDialogFragmentFragment;
          }) => {
            setShowCreateMeetingModal(false);
            link.redirect(
              `/meeting/${meeting?.meetingGroup?.id}/${meeting.id}`,
            );
          }}
          onClose={() => setShowCreateMeetingModal(false)}
        />
      )}
      <div className="fs-mask" aria-label="Daily meetings">
        <div className="sidebar-meeting">
          <div className="flex items-center justify-between pt-1 pb-3 pl-2 pr-1.5">
            <AppLink
              to="/calendar"
              className="text-gray-500 font-normal text-xs"
            >
              {moment(meetingDate).format("ddd, MMM DD")}
            </AppLink>
            <div className="flex items-center gap-1.5">
              {!moment(meetingDate).isSame(moment(), "day") && (
                <Tooltip delay={500} text="Jump to Today">
                  <button
                    onClick={handleClickToday}
                    className="text-gray-400 rounded hover:bg-gray-100 p-0.5"
                  >
                    <NotificationBox className="h-4 w-4" />
                  </button>
                </Tooltip>
              )}
              <Tooltip
                delay={500}
                text={moment(meetingDate)
                  .subtract(1, "days")
                  .format("dddd, MMMM D, YYYY")}
              >
                <button
                  onClick={handleClickPreviousDay}
                  className="text-gray-400 rounded hover:bg-gray-100 p-0.5"
                  aria-label="Sidebar previous day button"
                >
                  <ArrowNarrowLeft className="h-4 w-4" />
                </button>
              </Tooltip>
              <Tooltip
                delay={500}
                text={moment(meetingDate)
                  .add(1, "days")
                  .format("dddd, MMMM D, YYYY")}
              >
                <button
                  onClick={handleClickNextDay}
                  className="text-gray-400 rounded hover:bg-gray-100 p-0.5"
                  aria-label="Sidebar next day button"
                >
                  <ArrowNarrowRight className="h-4 w-4" />
                </button>
              </Tooltip>
              <Tooltip delay={500} text="Create Meeting">
                <button
                  onClick={() => setShowCreateMeetingModal(true)}
                  className="text-gray-400 rounded hover:bg-gray-100 p-0.5"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </Tooltip>
            </div>
          </div>

          {(loading && !data) || debouncedMeetingDate !== meetingDate ? (
            <div className="flex flex-col gap-2">
              {range(lastLoadingCount.current).map((i) => (
                <div
                  key={i}
                  className="bg-gray-100 border border-transparent rounded-md py-2 px-2.5 flex justify-between gap-3"
                >
                  <div className="skeleton text-xs flex-1 rounded-lg text-transparent">
                    loading
                  </div>
                  <div className="skeleton w-14 rounded-lg text-xs text-transparent">
                    12pm
                  </div>
                </div>
              ))}
            </div>
          ) : data ? (
            <div className="flex flex-col gap-2">
              <SidebarScheduledMeetingsList
                meetings={meetings}
                meetingDate={meetingDate}
              />
            </div>
          ) : null}
        </div>
        {data?.meetings?.pageInfo.hasNextPage && (
          <Link
            to="/calendar"
            className="block my-4 text-gray-600 text-sm hover:underline"
          >
            More meetings
          </Link>
        )}
        {unscheduledMeetings.length > 0 && (
          <div aria-label="Sidebar unscheduled meetings">
            <div className="flex items-center justify-between pt-2">
              <Tooltip text="Unscheduled meetings">
                <span className="text-gray-500 px-2 py-2 font-medium text-xs">
                  Unscheduled
                </span>
              </Tooltip>
            </div>
            <div className="flex flex-col gap-1.5">
              {unscheduledMeetings.map((meeting) => (
                <SidebarMeeting
                  key={meeting.id}
                  title={meeting.title}
                  videoConferenceUrl={meeting.videoConferenceUrl}
                  videoConferenceType={meeting.videoConferenceType}
                  meetingId={meeting.id}
                  meetingGroupId={meeting.meetingGroup?.id}
                  draft={meeting.draft}
                  startDatetime={meeting.startDatetime}
                  endDatetime={meeting.endDatetime}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SidebarMeetings;
